import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {NameProduct} from '../../ui';
import {AddToFavoritesButton} from './AddToFavoritesButton';
import {NumberInputUI} from '../../ui/components/NumberInputUI';
import {AvailabilityUI} from '../../common/ui/AvailabilityUI';
import {TagListUI} from '../../common/ui/TagListUI';

import {useProductSelection} from '../application/useProductSelection';
import {generateItemTags} from '../../common/CatalogService';
import {GtmService} from '../../infrastructure/analytics/GtmService';
import {Modal} from "react-bootstrap";
import {DealSelectorBis} from "./DealSelectorBis";
import ProductPurchase from "./ProductPurchase";
import {AddToCartButton} from "./AddToCartButton";
import {PendingProductButton} from "./PendingProductButton";
import {StockBypassNotice} from "./StockBypassNotice";
import {PriceWithFallback} from "../../common/ui/PriceWithFallback";
import {FormattedNumber} from "../../common/ui/FormattedNumber";
import {bestPriceForQuantity, isFreeComercialOnly, nextQuantityOffer} from "../domain/ProductSelection.model";
import {DealType} from "../domain/DealSelection.model";
import {useCartContext} from "../../cart/context/CartContext";
import {useUserContext} from "../../user/infrastructure/UserContext";

/**
 * @param {boolean} value
 * @param {Function} setHover
 * @returns {Function}
 */
const createHoverHandler = (value, setHover) => () => setHover(value);

/**
 * @param {Function} navigate
 * @param {Object} details
 * @returns {Function}
 */
const createNavigateHandler = (navigate, details) =>
    () => {
        GtmService.logSelectItem(details, window.location.pathname);
        navigate(`/articulos/${encodeURIComponent(details.code)}`);
    };

/**
 * Línea principal del producto en el carrito (no obsequio ni componente de pack) y su obsequio de promo.
 * @returns {{quantity: number, lotCode: string|null, giftId: number|null, giftQuantity: number|null}|null}
 */
const findCartLine = (cart, productId) => {
    const lines = [...(cart?.products ?? []), ...(cart?.deprecatedProducts ?? [])];
    const line = lines.find(it => it?.id === productId && it.packParentId == null);
    if (!line) {
        return null;
    }
    const gift = lines.find(it => it?.packParentId === productId && !it.packOrigin);
    return {
        quantity: line.quantity,
        lotCode: line.codigoLote || null,
        giftId: gift?.id ?? null,
        giftQuantity: gift?.quantity ?? null
    };
};

/**
 * Trato con el que se añadió la línea del carrito, si sigue en la ficha: promo por su obsequio
 * (la que cuadra con las cantidades si hay varias) o lote de oferta por su código.
 */
const findCartDeal = (details, quantity, lotCode, giftId, giftQuantity) => {
    if (giftId != null) {
        const promos = (details.promos ?? []).filter(promo => promo.promoProductId === giftId);
        const promo = promos.find(it => it.minPurchaseForPromo > 0
                && Math.floor(quantity / it.minPurchaseForPromo) * it.stock === giftQuantity)
            ?? promos[0];
        if (promo) {
            return {type: DealType.PROMO, value: promo};
        }
    }
    if (lotCode) {
        const lot = (details.lots ?? []).find(it => it.code === lotCode);
        if (lot) {
            return {type: DealType.LOT, value: lot};
        }
    }
    return null;
};

/**
 * @param {Object} props
 * @param {Object} props.details
 * @returns {JSX.Element}
 */
export const ProductFlatPurchaseCardUI = ({details}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [modal, setModal] = useState(false);

    const navigate = useNavigate();
    const user = useUserContext();
    const {cart} = useCartContext();
    const {
        productSelection,
        dealSelection,
        setDealSelection,
        setQuantity,
        manualLot,
        setManualLot
    } = useProductSelection(details, 1);

    const cartLine = useMemo(() => findCartLine(cart, details.id), [cart, details.id]);
    const cartQuantity = cartLine?.quantity;
    const cartLotCode = cartLine?.lotCode ?? null;
    const cartGiftId = cartLine?.giftId ?? null;
    const cartGiftQuantity = cartLine?.giftQuantity ?? null;
    // Un lote de la línea que no está en oferta en la ficha solo se conserva si lo eligió a mano un
    // comercial con permiso (el api rechaza los demás)
    const puedeConservarLote = user?.elegirLote === true;

    // La fila parte de lo que hay en el carrito: misma cantidad y, si sigue disponible, el mismo trato
    useEffect(() => {
        if (cartQuantity == null) {
            setDealSelection(null);
            setManualLot(null);
            setQuantity(1);
            return;
        }
        const deal = findCartDeal(details, cartQuantity, cartLotCode, cartGiftId, cartGiftQuantity);
        setDealSelection(deal);
        setManualLot(!deal && cartLotCode && puedeConservarLote ? cartLotCode : null);
        setQuantity(cartQuantity);
    }, [details, cartQuantity, cartLotCode, cartGiftId, cartGiftQuantity, puedeConservarLote,
        setDealSelection, setManualLot, setQuantity]);

    // Lotes de corta caducidad y promos X+Y se eligen en la ventana, y también lo que ya está en el
    // carrito para no pisar su línea. Las ofertas por cantidad las aplica el servidor al añadir.
    const needsDealSelector = !!cartLine || details.lots?.length > 0 || details.promos?.length > 0;
    const openSelector = useCallback(() => setModal(true), []);

    const tags = useMemo(() => generateItemTags(details), [details]);

    const handleMouseEnter = useMemo(() => createHoverHandler(true, setIsHovered), []);
    const handleMouseLeave = useMemo(() => createHoverHandler(false, setIsHovered), []);
    const handleNavigate = useMemo(() => createNavigateHandler(navigate, details), [navigate, details]);

    const hasStock = useMemo(() =>
            (details.availableUnits > 0 || details.onDemand),
        [details.availableUnits, details.onDemand]
    );

    // Precio por unidad para la cantidad elegida y siguiente escalón de oferta por cantidad
    const {rowPrice, nextOffer} = useMemo(() => {
        if (isFreeComercialOnly(details)) {
            return {rowPrice: {sellingPrice: 0, basePrice: 0}, nextOffer: null};
        }
        if (dealSelection?.type === DealType.LOT || dealSelection?.type === DealType.PROMO) {
            return {rowPrice: productSelection.price, nextOffer: null};
        }
        const quantity = Number(productSelection.quantity) || 0;
        const price = bestPriceForQuantity(details.price, details.offers, quantity);
        return {
            rowPrice: {sellingPrice: price?.bestPrice, basePrice: price?.value},
            nextOffer: nextQuantityOffer(details.price, details.offers, quantity)
        };
    }, [details, dealSelection, productSelection]);

    const dealData = React.useMemo(() => ({
        offers: details.offers,
        lots: details.lots,
        promos: details.promos,
        hasDealsToChoose: details.hasDealsToChoose
    }), [details]);

    const recap = React.useMemo(() => {
        if (!productSelection?.price || !productSelection?.discount) {
            return;
        }
        return ({
            price: {
                value: productSelection.price.basePrice * productSelection.quantity,
                bestPrice: productSelection.price.sellingPrice * productSelection.quantity
            },
            discount: {
                value: productSelection.discount.value,
                percentage: productSelection.discount.percentage,
                line: productSelection.discount.text
            }
        })
    }, [productSelection]);

    // Avisos de lo que lleva la línea del carrito y se perdería al actualizar sin elegirlo
    const cartNotices = [];
    if (manualLot && productSelection.lotCode === manualLot) {
        cartNotices.push(<small key="lot" className="text-muted">Se mantiene el lote <strong>{manualLot}</strong> de la línea del carrito.</small>);
    } else if (cartLotCode && productSelection.lotCode !== cartLotCode) {
        cartNotices.push(<small key="lot" className="text-warning">La línea del carrito lleva el lote <strong>{cartLotCode}</strong>: al actualizar sin elegirlo se quitará.</small>);
    }
    if (cartGiftId != null && dealSelection?.type !== DealType.PROMO) {
        cartNotices.push(<small key="gift" className="text-warning">La línea del carrito lleva un obsequio de promoción: al actualizar sin elegir la promoción se quitará.</small>);
    }

    return (
        <div
            className="rounded-2 p-1 shadow-sm"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="row justify-content-start align-items-center">
                <div className="col-12 col-md-2 col-xl-auto">
                    <div className="d-flex justify-content-center align-items-center">
                        <div className="position-absolute">
                            <AddToFavoritesButton
                                data={{
                                    productCode: details.code,
                                    show: isHovered,
                                    isFavorite: details.isFavorite,
                                }}
                            />
                        </div>
                        <img
                            src={details.imageUrl}
                            onClick={handleNavigate}
                            style={{height: '65px', width: '65px', objectFit: 'contain'}}
                            className="rounded"
                            alt="Imagen del producto"
                        />
                    </div>
                </div>

                <div className="col-12 col-md-8 col-xl">
                    <div className="row gap-2 gap-lg-0">
                        <div className="col-12 col-lg-8 col-xl-8">
                            <div
                                className="d-flex flex-row justify-content-between"
                                style={{cursor: 'pointer'}}
                                onClick={handleNavigate}
                            >
                                <div className="d-flex flex-column">
                                    <TagListUI data={{tags}}/>
                                    <NameProduct>
                                        {details.name}
                                        <small className="text-muted ms-1">Ref: {details.code}</small>
                                    </NameProduct>
                                    <AvailabilityUI data={{availability: productSelection.availability}}/>
                                </div>
                                <div className="d-flex flex-column align-items-end text-end">
                                    <PriceWithFallback data={rowPrice}/>
                                    {nextOffer && (
                                        <small className="text-muted text-nowrap">
                                            desde <FormattedNumber value={nextOffer.price.bestPrice} config={{style: "currency"}}/> a
                                            partir de {nextOffer.minQuantity} uds
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="col-12 col-lg-4 col-xl-4">
                            <div className="d-flex flex-row gap-2 justify-content-end pe-2">
                                {(details.isPending || !hasStock) && !details.stockBypass && (
                                    <PendingProductButton
                                        productCode={details.code}
                                        defaultValue={details.isPending}
                                        quantity={productSelection.quantity}
                                    />
                                )}
                                <NumberInputUI
                                    data={{value: productSelection.quantity}}
                                    actions={{setValue: setQuantity}}
                                />
                                <div>
                                    <Modal show={modal} onHide={() => setModal(false)} centered size="lg">
                                        <Modal.Header closeButton>
                                            <Modal.Title>{details.name}</Modal.Title>
                                        </Modal.Header>
                                        <Modal.Body>
                                            <DealSelectorBis dealData={dealData} selection={dealSelection}
                                                             onSelectionChange={setDealSelection}/>
                                            {cartNotices.length > 0 && (
                                                <div className="mt-3 d-flex flex-column gap-1">{cartNotices}</div>
                                            )}
                                            {/* Igual que en la ficha: sin aviso de bajo pedido si hay stock */}
                                            {!(details.availableUnits > 0) &&
                                                <StockBypassNotice product={details} className="mt-3 mb-0" />
                                            }
                                        </Modal.Body>
                                        <Modal.Footer className="d-block">
                                            <ProductPurchase recap={recap}>
                                                <ProductPurchase.PurchaseControls>
                                                    <div
                                                        className="d-flex gap-4 align-items-center justify-content-center justify-content-xl-end">
                                                        <NumberInputUI data={{value: productSelection.quantity}} actions={{setValue: setQuantity}}/>
                                                        <AddToCartButton productSelection={productSelection} product={details} afterAddToCart={() => setModal(false)}/>
                                                    </div>
                                                </ProductPurchase.PurchaseControls>
                                            </ProductPurchase>
                                        </Modal.Footer>
                                    </Modal>
                                    <div style={{minWidth: '135px'}}>
                                        <AddToCartButton productSelection={productSelection} product={details}
                                                         onOpenSelector={needsDealSelector ? openSelector : undefined}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
